// =============================================================================
// TEXT SECTION COMPONENT
// =============================================================================

import React from 'react';
import type { ContentComponent } from '@/types';

interface TextSectionProps {
  component: ContentComponent;
}

/**
 * Text Section Component
 */
export const TextSection: React.FC<TextSectionProps> = ({ component }) => {
  return <TextSectionImpl component={component} />;
};

/**
 * Text Section Implementation
 */
const TextSectionImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const textAlign = metadata?.textAlign || 'left';
  const maxWidth = metadata?.maxWidth || 'none';
  const image = metadata?.image;
  const imagePosition = (metadata?.imagePosition || 'left') as 'left' | 'right' | 'top';
  const imageAlt = metadata?.imageAlt || title || 'Text section image';
  const imageCaption = metadata?.imageCaption;
  const imageCredit = metadata?.imageCredit;
  
  const contentStyle: React.CSSProperties = {
    textAlign: textAlign as React.CSSProperties['textAlign'],
    maxWidth: maxWidth !== 'none' ? `${maxWidth}px` : 'none',
    margin: maxWidth !== 'none' ? '0 auto' : '0',
  };
  
  // Check if we have an image to display
  if (image) {
    // Handle top image position (image above text)
    if (imagePosition === 'top') {
      return (
        <section id={id} className="content-section text-section">
          <div className="container">
            <div className="text-section-with-image-top">
              {/* Image Above */}
              <div className="text-section-image-top">
                <div className="text-section-img-wrapper">
                  <img 
                    src={image} 
                    alt={imageAlt}
                    className="text-section-img"
                  />
                  {imageCredit && (
                    <div className="text-section-image-credit">
                      <span dangerouslySetInnerHTML={{ __html: imageCredit }} />
                    </div>
                  )}
                </div>
                {imageCaption && (
                  <p className="text-section-caption">{imageCaption}</p>
                )}
              </div>
              
              {/* Text Below */}
              <div className="text-section-content" style={contentStyle}>
                {title && <h2 className="heading-style">{title}</h2>}
                <div 
                  className="text-content"
                  dangerouslySetInnerHTML={{ __html: content }}
                  style={{ lineHeight: '1' }}
                />
              </div>
            </div>
          </div>
        </section>
      );
    }
    
    // Handle left/right image positions (side by side)
    return (
      <section id={id} className="content-section text-section">
        <div className="container">
          <div className={`text-section-with-image ${imagePosition === 'right' ? 'image-right' : 'image-left'}`}>
            {/* Image Column */}
            <div className="text-section-image">
              <div className="text-section-img-wrapper">
                <img 
                  src={image} 
                  alt={imageAlt}
                  className="text-section-img"
                />
                {imageCredit && (
                  <div className="text-section-image-credit">
                    <span dangerouslySetInnerHTML={{ __html: imageCredit }} />
                  </div>
                )}
              </div>
              {imageCaption && (
                <p className="text-section-caption">{imageCaption}</p>
              )}
            </div>
            
            {/* Text Column */}
            <div className="text-section-content" style={contentStyle}>
              {title && <h2 className="heading-style">{title}</h2>}
              <div 
                className="text-content"
                dangerouslySetInnerHTML={{ __html: content }}
                style={{ lineHeight: '1' }}
              />
            </div>
          </div>
        </div>
      </section>
    );
  }
  
  // Render single column layout (original behavior)
  return (
    <section id={id} className="content-section text-section">
      <div className="container">
        <div style={contentStyle}>
          {title && <h2 className="heading-style">{title}</h2>}
          <div 
            className="text-content"
            dangerouslySetInnerHTML={{ __html: content }}
            style={{ lineHeight: '1' }}
          />
        </div>
      </div>
    </section>
  );
};

export { TextSectionImpl };

